import type { RevenueSourceView } from '../components/RevenueBreakdownCard';

export type FinanceCsvKpi = {
  label: string;
  amount: number | null;
  delta: number | null;
};

export type FinanceCsvSeries = {
  name: string;
  values: number[];
};

export type FinanceCsvCountry = {
  label: string;
  countryCode: string;
  amount: number;
  percentage: number;
};

export type FinanceCsvInput = {
  currency: string;
  rangeLabel: string;
  days: number | null;
  countryCode: string;
  startDate?: string;
  endDate?: string;
  kpis: FinanceCsvKpi[];
  revenueSources: RevenueSourceView[];
  countryBreakdown: FinanceCsvCountry[];
  overviewCategories: string[];
  overviewSeries: FinanceCsvSeries[];
  revenueTrendPeriods?: string[];
  revenueTrendSeries?: FinanceCsvSeries[];
  topCreators?: { userId: number; displayName: string; amount: number }[];
  paymentChannels?: {
    provider: string;
    label: string;
    gross: number;
    providerFee: number | null;
    refundsChargebacks?: number | null;
    refundsChargebacksAvailability?: string;
    netSettlement: number | null;
    providerFeeAvailability: string;
    netSettlementAvailability: string;
  }[];
  payoutStatus?: {
    paid: number | null;
    pending: number | null;
    failed: number | null;
    onHold: number | null;
  };
  riskUnderReview?: number | null;
  operatingExpensePeriods?: string[];
  operatingExpenseSeries?: FinanceCsvSeries[];
  creatorIncentivesReferral?: number | null;
};

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function section(title: string, headers: string[], rows: (string | number | null | undefined)[][]): string[] {
  const lines = [csvEscape(title), headers.map(csvEscape).join(',')];
  for (const row of rows) {
    lines.push(row.map(csvEscape).join(','));
  }
  lines.push('');
  return lines;
}

/** Builds a multi-section CSV reflecting the currently displayed Finance dashboard. */
export function buildFinanceDashboardCsv(input: FinanceCsvInput): string {
  const countryLabel = input.countryCode || 'All creator countries';
  const lines: string[] = [
    ...section(
      'Filters',
      ['currency', 'range', 'days', 'creatorCountryConnect', 'startDate', 'endDate'],
      [
        [
          input.currency,
          input.rangeLabel,
          input.days == null ? 'all' : input.days,
          countryLabel,
          input.startDate ?? '',
          input.endDate ?? '',
        ],
      ],
    ),
    ...section(
      'KPIs',
      ['label', 'amount', 'deltaPercent'],
      input.kpis.map((kpi) => [kpi.label, kpi.amount, kpi.delta]),
    ),
    ...section(
      'Revenue sources',
      ['name', 'category', 'amount', 'percentage'],
      input.revenueSources.map((source) => [
        source.name,
        source.category ?? '',
        source.amount,
        source.percentage,
      ]),
    ),
    ...section(
      'Creator country (Connect) gross breakdown',
      ['countryCode', 'label', 'amount', 'percentage'],
      input.countryBreakdown.map((row) => [
        row.countryCode,
        row.label,
        row.amount,
        row.percentage,
      ]),
    ),
  ];

  if (input.overviewCategories.length > 0 && input.overviewSeries.length > 0) {
    lines.push(
      ...section(
        'Gross Transaction Value Overview',
        ['period', ...input.overviewSeries.map((series) => series.name)],
        input.overviewCategories.map((period, index) => [
          period,
          ...input.overviewSeries.map((series) => series.values[index] ?? 0),
        ]),
      ),
    );
  }

  if (
    (input.revenueTrendPeriods?.length ?? 0) > 0 &&
    (input.revenueTrendSeries?.length ?? 0) > 0
  ) {
    lines.push(
      ...section(
        'Revenue source trends',
        ['period', ...(input.revenueTrendSeries ?? []).map((series) => series.name)],
        (input.revenueTrendPeriods ?? []).map((period, index) => [
          period,
          ...(input.revenueTrendSeries ?? []).map((series) => series.values[index] ?? 0),
        ]),
      ),
    );
  }

  if ((input.topCreators?.length ?? 0) > 0) {
    lines.push(
      ...section(
        'Top creators',
        ['userId', 'displayName', 'amount'],
        (input.topCreators ?? []).map((creator) => [
          creator.userId,
          creator.displayName,
          creator.amount,
        ]),
      ),
    );
  }

  if ((input.paymentChannels?.length ?? 0) > 0) {
    lines.push(
      ...section(
        'Payment channels',
        [
          'provider',
          'label',
          'gross',
          'providerFee',
          'providerFeeAvailability',
          'refundsChargebacks',
          'refundsChargebacksAvailability',
          'netSettlement',
          'netSettlementAvailability',
        ],
        (input.paymentChannels ?? []).map((channel) => [
          channel.provider,
          channel.label,
          channel.gross,
          channel.providerFee,
          channel.providerFeeAvailability,
          channel.refundsChargebacks ?? null,
          channel.refundsChargebacksAvailability ?? 'COMING_SOON',
          channel.netSettlement,
          channel.netSettlementAvailability,
        ]),
      ),
    );
  }

  if (input.payoutStatus) {
    lines.push(
      ...section(
        'Creator Payout status summary',
        ['paid', 'pending', 'failed', 'onHold'],
        [
          [
            input.payoutStatus.paid,
            input.payoutStatus.pending,
            input.payoutStatus.failed,
            input.payoutStatus.onHold,
          ],
        ],
      ),
    );
  }

  if (
    (input.operatingExpensePeriods?.length ?? 0) > 0 &&
    (input.operatingExpenseSeries?.length ?? 0) > 0
  ) {
    lines.push(
      ...section(
        'Operating expense trends',
        ['period', ...(input.operatingExpenseSeries ?? []).map((series) => series.name)],
        (input.operatingExpensePeriods ?? []).map((period, index) => [
          period,
          ...(input.operatingExpenseSeries ?? []).map((series) => series.values[index] ?? 0),
        ]),
      ),
    );
  }

  if (input.creatorIncentivesReferral != null || input.riskUnderReview != null) {
    lines.push(
      ...section(
        'Incentives and risk',
        ['creatorIncentivesReferral', 'riskUnderReview'],
        [[input.creatorIncentivesReferral ?? '', input.riskUnderReview ?? '']],
      ),
    );
  }

  return lines.join('\n');
}

export function financeCsvFilename(currency: string, days: number | null, countryCode?: string): string {
  const country = countryCode ? `-${countryCode.toLowerCase()}` : '';
  const stamp = new Date().toISOString().slice(0, 10);
  const window = days == null ? 'all' : `${days}d`;
  return `tipmi-finance-${currency.toLowerCase()}-${window}${country}-${stamp}.csv`;
}

export function downloadTextFile(filename: string, contents: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
